/**
 * Phase K Pass 1 — Wrapper around dashboard cards used to render the
 * customize-mode hide/show overlays without altering the original card
 * markup (or its existing data-testid attributes).
 *
 * Modes:
 *  - customizeMode=false, hidden=false → render children as-is.
 *  - customizeMode=false, hidden=true  → render nothing.
 *  - customizeMode=true,  hidden=false → render children + corner "Hide" pill.
 *  - customizeMode=true,  hidden=true  → render ghosted placeholder + "Show" pill.
 */
import React from "react";
import { Eye, EyeOff } from "lucide-react";

export function DashboardCardShell({
    cardKey,
    label,
    hidden,
    customizeMode,
    onToggle,
    children,
    wide = false,
}) {
    const colSpan = wide ? "col-span-1 md:col-span-2 lg:col-span-3" : "";

    if (!customizeMode && hidden) return null;

    if (!customizeMode) {
        return (
            <div
                data-testid={`dashboard-card-shell-${cardKey}`}
                className={colSpan}
            >
                {children}
            </div>
        );
    }

    if (hidden) {
        return (
            <div
                data-testid={`dashboard-card-shell-${cardKey}`}
                data-card-state="hidden"
                className={`${colSpan} relative h-full min-h-[140px] rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5 flex flex-col items-center justify-center gap-3`}
            >
                <div className="text-center">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Hidden</p>
                    <p className="mt-1 text-sm font-medium text-slate-700">{label}</p>
                </div>
                <button
                    type="button"
                    onClick={() => onToggle(cardKey, false)}
                    data-testid={`dashboard-card-show-${cardKey}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
                >
                    <Eye size={12} /> Show
                </button>
            </div>
        );
    }

    return (
        <div
            data-testid={`dashboard-card-shell-${cardKey}`}
            data-card-state="visible"
            className={`${colSpan} relative`}
        >
            <button
                type="button"
                onClick={() => onToggle(cardKey, true)}
                data-testid={`dashboard-card-hide-${cardKey}`}
                className="absolute top-2 right-2 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/95 border border-slate-300 px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm backdrop-blur-sm"
                aria-label={`Hide ${label}`}
            >
                <EyeOff size={12} /> Hide
            </button>
            {children}
        </div>
    );
}

export default DashboardCardShell;
